import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';

const BlogForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        author: '',
        date: new Date().toLocaleDateString('tr-TR'),
        summary: '',
        content: '',
        image: ''
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchBlog = async () => {
                setLoading(true);
                const docRef = doc(db, "blogs", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setFormData(docSnap.data());
                } else {
                    alert("Yazı bulunamadı!");
                    navigate('/admin/blogs');
                }
                setLoading(false);
            };
            fetchBlog();
        }
    }, [id, isEditMode, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const storageRef = ref(storage, `blogs/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);
            setFormData(prev => ({ ...prev, image: url }));
        } catch (error) {
            console.error("Resim yükleme hatası:", error);
            alert("Resim yüklenirken hata oluştu.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const dataToSave = {
                ...formData,
                updatedAt: new Date().toISOString()
            };

            if (isEditMode) {
                await updateDoc(doc(db, "blogs", id), dataToSave);
            } else {
                await addDoc(collection(db, "blogs"), {
                    ...dataToSave,
                    createdAt: new Date().toISOString()
                });
            }
            navigate('/admin/blogs');
        } catch (error) {
            console.error("Kayıt hatası:", error);
            alert("Kaydedilirken bir hata oluştu: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEditMode && !formData.title) return <div className="text-white">Yükleniyor...</div>;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/admin/blogs')}
                    className="p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors"
                >
                    <ArrowLeft size={24} />
                </button>
                <h1 className="text-2xl font-bold text-white">
                    {isEditMode ? 'Köşe Yazısını Düzenle' : 'Yeni Köşe Yazısı Ekle'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-6">

                {/* Title */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Başlık</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        placeholder="Örn: Turist Rehberliğinin Geleceği"
                    />
                </div>

                {/* Author & Date */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Yazar</label>
                        <input
                            type="text"
                            name="author"
                            value={formData.author}
                            onChange={handleChange}
                            required
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                            placeholder="Ad Soyad"
                        />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Yazar Unvanı/Rolü</label>
                        <input
                            type="text"
                            name="authorRole"
                            value={formData.authorRole || ''}
                            onChange={handleChange}
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                            placeholder="Örn: Turizm Rehberliği 2. Sınıf"
                        />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Tarih</label>
                        <input
                            type="text"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            required
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                            placeholder="DD.MM.YYYY"
                        />
                    </div>
                </div>

                {/* Image Upload */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Kapak Görseli</label>
                    <div className="flex flex-col md:flex-row items-start gap-6">
                        {formData.image && (
                            <div className="relative w-full md:w-32 h-44 md:h-32 rounded-lg overflow-hidden border border-gray-600 group">
                                <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                                    className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-red-500"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        )}
                        <div className="flex-1">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer hover:bg-gray-700/50 hover:border-cyan-500 transition-all">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Upload className="w-8 h-8 mb-2 text-gray-400" />
                                    <p className="text-sm text-gray-400"><span className="font-semibold">Resim yüklemek için tıklayın</span></p>
                                    <p className="text-xs text-gray-500">PNG, JPG, GIF (Max 5MB)</p>
                                </div>
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                            </label>
                        </div>
                    </div>
                </div>

                {/* Manual Image URL Input */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Veya Resim URL</label>
                    <input
                        type="text"
                        name="image"
                        value={formData.image || ''}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                        placeholder="Örn: https://example.com/image.jpg veya /assets/image.jpg"
                    />
                </div>

                {/* Summary */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Kısa Özet (Listeleme için)</label>
                    <textarea
                        name="summary"
                        value={formData.summary}
                        onChange={handleChange}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors h-24 resize-none"
                        placeholder="Yazının kısa bir özeti..."
                    />
                </div>

                {/* Content (HTML/RichText placeholder) */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">İçerik (Düz Metin)</label>
                    <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        required
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors font-mono text-sm h-64"
                        placeholder="Yazınızı buraya düz metin olarak girin. Paragraflar arasında boşluk bırakabilirsiniz."
                    />
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50"
                    >
                        <Save size={20} />
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BlogForm;
